/**
 * Guide Booking Detail Page
 *
 * Shows full booking details from the guide's perspective:
 * - Customer info (name, email, phone for confirmed bookings)
 * - Experience details (title, date, time, group size)
 * - Booking status with actions (accept/decline for pending, cancel for confirmed)
 * - Payment summary
 * - Special requests / notes
 *
 * Epic: Cleanup Sprint 3
 * Story: SCRUM-822 — FIX-E04: Guide booking detail route & page (5 pts)
 *
 * Data Flow:
 * - Fetches from GET /api/v1/bookings/:bookingId/detail
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import { formatCentsToDollars } from '../../utils/formHelpers';
import {
  Calendar,
  Clock,
  Users,
  DollarSign,
  Phone,
  Mail,
  MapPin,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowLeft,
  FileText
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';
import ConfirmModal from '../../components/shared/ConfirmModal';
import BookingCancellationGuide from '../../components/guide/BookingCancellationGuide';
import { logError } from '../../services/errorLoggingService';

export default function GuideBookingDetail() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [declineConfirm, setDeclineConfirm] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [taxInfo, setTaxInfo] = useState(null);

  const fetchBookingDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`/api/v1/bookings/${bookingId}/detail`, { method: 'GET' });
      if (response.success) {
        setBooking(response.data);
      }
    } catch (err) {
      logError(err, { componentName: 'GuideBookingDetail', action: 'fetch_booking' });
      if (err.status === 404) {
        setError('Booking not found');
      } else if (err.status === 403) {
        setError('You do not have access to this booking');
      } else if (err.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to load booking details');
      }
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    fetchBookingDetail();
  }, [fetchBookingDetail]);

  useEffect(() => {
    if (!bookingId) { return; }
    apiRequest(`/api/v1/guides/accounting/bookings/${bookingId}/tax`, { method: 'GET' })
      .then(res => { if (res.success) { setTaxInfo(res.data); } })
      .catch(() => {});
  }, [bookingId]);

  const handleAcceptBooking = async () => {
    try {
      const response = await apiRequest(`/api/v1/bookings/${bookingId}/status`, {
        method: 'POST',
        body: JSON.stringify({ status: 'confirmed' })
      });
      if (response.success) {
        await fetchBookingDetail();
        setSuccessMessage('Booking confirmed!');
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err) {
      logError(err, { componentName: 'GuideBookingDetail', action: 'accept_booking' });
      setErrorMessage(err.message || 'Failed to accept booking');
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  const handleDeclineBooking = async () => {
    setDeclineConfirm(false);
    try {
      const response = await apiRequest(`/api/v1/bookings/${bookingId}/status`, {
        method: 'POST',
        body: JSON.stringify({ status: 'cancelled' })
      });
      if (response.success) {
        await fetchBookingDetail();
        setSuccessMessage('Booking declined.');
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err) {
      logError(err, { componentName: 'GuideBookingDetail', action: 'decline_booking' });
      setErrorMessage(err.message || 'Failed to decline booking');
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  const formatCurrency = formatCentsToDollars;

  const formatDate = (dateString) => {
    if (!dateString) { return 'TBD'; }
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) { return 'TBD'; }
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: 'Pending', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200', textColor: 'text-yellow-800', icon: AlertCircle, iconColor: 'text-yellow-600', message: 'This booking is awaiting your confirmation.' },
      confirmed: { label: 'Confirmed', bgColor: 'bg-green-50', borderColor: 'border-green-200', textColor: 'text-green-800', icon: CheckCircle, iconColor: 'text-green-600', message: 'This booking is confirmed. Prepare for the trip!' },
      paid: { label: 'Paid', bgColor: 'bg-blue-50', borderColor: 'border-blue-200', textColor: 'text-blue-800', icon: DollarSign, iconColor: 'text-blue-600', message: 'Payment received for this booking.' },
      completed: { label: 'Completed', bgColor: 'bg-gray-50', borderColor: 'border-gray-200', textColor: 'text-gray-800', icon: CheckCircle, iconColor: 'text-gray-600', message: 'This trip has been completed.' },
      cancelled: { label: 'Cancelled', bgColor: 'bg-red-50', borderColor: 'border-red-200', textColor: 'text-red-800', icon: XCircle, iconColor: 'text-red-600', message: 'This booking has been cancelled.' },
      refunded: { label: 'Refunded', bgColor: 'bg-orange-50', borderColor: 'border-orange-200', textColor: 'text-orange-800', icon: DollarSign, iconColor: 'text-orange-600', message: 'This booking has been refunded.' }
    };
    return configs[status] || configs.pending;
  };

  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userRole="guide" activeNavId="bookings" onNavigate={handleNavigate}>
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner message="Loading booking details..." />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !booking) {
    return (
      <DashboardLayout userRole="guide" activeNavId="bookings" onNavigate={handleNavigate}>
        <div className="space-y-4">
          <button
            onClick={() => navigate('/guide/bookings')}
            className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Bookings</span>
          </button>
          <Alert variant="error" message={error || 'Booking not found'} />
        </div>
      </DashboardLayout>
    );
  }

  const statusConfig = getStatusConfig(booking.status);
  const StatusIcon = statusConfig.icon;
  const canAcceptDecline = booking.status === 'pending';
  const canCancel = ['confirmed', 'paid'].includes(booking.status) && new Date(booking.start_date) > new Date();

  return (
    <DashboardLayout userRole="guide" activeNavId="bookings" onNavigate={handleNavigate}>
      <div className="space-y-6">
        {/* Messages */}
        {successMessage && (
          <Alert variant="success" message={successMessage} />
        )}
        {errorMessage && (
          <Alert variant="error" message={errorMessage} />
        )}

        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/guide/bookings')}
              className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900 mb-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Bookings</span>
            </button>
            <h1 className="text-2xl font-bold text-gray-900">{booking.experience_title}</h1>
            <p className="mt-1 text-sm text-gray-500">
              Booking #{booking.booking_number || booking.id.slice(0, 8)}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {canAcceptDecline && (
              <>
                <button
                  onClick={handleAcceptBooking}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>Accept</span>
                </button>
                <button
                  onClick={() => setDeclineConfirm(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  <XCircle className="w-4 h-4" />
                  <span>Decline</span>
                </button>
              </>
            )}
            {canCancel && (
              <button
                onClick={() => setCancelModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                <XCircle className="w-4 h-4" />
                <span>Cancel Booking</span>
              </button>
            )}
          </div>
        </div>

        {/* Status Banner */}
        <div className={`p-4 rounded-lg border ${statusConfig.bgColor} ${statusConfig.borderColor}`}>
          <div className="flex items-center space-x-2">
            <StatusIcon className={`w-5 h-5 ${statusConfig.iconColor}`} />
            <p className={`text-sm font-medium ${statusConfig.textColor}`}>
              {statusConfig.message}
            </p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Booking Details */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Booking Details</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Date</p>
                    <p className="font-medium text-gray-900">{formatDate(booking.start_date)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Time</p>
                    <p className="font-medium text-gray-900">{formatTime(booking.start_time)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Users className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Group Size</p>
                    <p className="font-medium text-gray-900">
                      {booking.participant_count || 1} participant{(booking.participant_count || 1) !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                {booking.meeting_location_name && (
                  <div className="flex items-center space-x-3">
                    <MapPin className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Meeting Point</p>
                      <p className="font-medium text-gray-900">{booking.meeting_location_name}</p>
                    </div>
                  </div>
                )}
                {booking.duration_hours && (
                  <div className="flex items-center space-x-3">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Duration</p>
                      <p className="font-medium text-gray-900">{booking.duration_hours} hour{booking.duration_hours !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Experience Description */}
            {booking.experience_description && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Experience Description</h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {booking.experience_description}
                </p>
              </div>
            )}

            {/* Special Requests / Notes */}
            {booking.special_requests && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <span>Special Requests</span>
                </h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {booking.special_requests}
                </p>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Customer Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer</h2>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold">
                    {booking.customer_first_name?.[0] || '?'}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {booking.customer_first_name} {booking.customer_last_name}
                    </p>
                  </div>
                </div>

                {['confirmed', 'paid', 'completed'].includes(booking.status) && (
                  <div className="pt-3 border-t border-gray-200 space-y-2">
                    {booking.customer_email && (
                      <a
                        href={`mailto:${booking.customer_email}`}
                        className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <Mail className="w-4 h-4" />
                        <span>{booking.customer_email}</span>
                      </a>
                    )}
                    {booking.customer_phone && (
                      <a
                        href={`tel:${booking.customer_phone}`}
                        className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <Phone className="w-4 h-4" />
                        <span>{booking.customer_phone}</span>
                      </a>
                    )}
                  </div>
                )}

                {booking.status === 'pending' && (
                  <p className="text-xs text-gray-500 italic">
                    Contact info available after confirmation
                  </p>
                )}
              </div>
            </div>

            {/* Payment Summary */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Summary</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total</span>
                  <span className="text-xl font-bold text-gray-900">
                    {formatCurrency(booking.total_price_cents)}
                  </span>
                </div>
                {booking.experience_base_price && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Base price per person</span>
                    <span className="text-gray-600">{formatCurrency(booking.experience_base_price)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Tax Information */}
            {taxInfo && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Tax Information</h2>
                {taxInfo.tax_amount_cents > 0 ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Tax collected</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(taxInfo.tax_amount_cents)}</span>
                    </div>
                    {taxInfo.tax_jurisdiction_code && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Jurisdiction</span>
                        <span className="text-gray-600">{taxInfo.tax_jurisdiction_code}</span>
                      </div>
                    )}
                    {taxInfo.tax_rate !== null && taxInfo.tax_rate !== undefined && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Effective rate</span>
                        <span className="text-gray-600">{(taxInfo.tax_rate * 100).toFixed(2)}%</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                      Sales tax collected and remitted by OutdoorGuides on your behalf.
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No tax collected on this booking.</p>
                )}
              </div>
            )}

            {/* Experience Info */}
            <Alert variant="info">
              <h3 className="text-sm font-semibold mb-2">Experience Info</h3>
              <div className="text-sm space-y-1">
                {booking.difficulty_level && (
                  <p>Difficulty: <span className="font-medium capitalize">{booking.difficulty_level}</span></p>
                )}
                {booking.minimum_age && (
                  <p>Minimum age: <span className="font-medium">{booking.minimum_age}+</span></p>
                )}
                {booking.max_participants && (
                  <p>Max group: <span className="font-medium">{booking.max_participants}</span></p>
                )}
              </div>
            </Alert>
          </div>
        </div>

        {/* Decline Confirmation Modal */}
        <ConfirmModal
          isOpen={declineConfirm}
          onClose={() => setDeclineConfirm(false)}
          onConfirm={handleDeclineBooking}
          title="Decline Booking"
          message="Are you sure you want to decline this booking? The customer will be notified."
          confirmText="Decline"
          cancelText="Keep Booking"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
        />

        {/* Cancel Booking Modal */}
        <BookingCancellationGuide
          isOpen={cancelModal}
          onClose={() => setCancelModal(false)}
          bookingId={bookingId}
          onCancelled={() => fetchBookingDetail()}
        />
      </div>
    </DashboardLayout>
  );
}
